<template>
  <div>
    <v-row>
      <v-col>
        <h4>{{ $t('host.files.title') }}</h4>
      </v-col>
    </v-row>

    <v-row v-if="isLoading">
      <v-col>
        <v-progress-linear indeterminate color="primary" />
      </v-col>
    </v-row>

    <v-row v-else-if="!files.length">
      <v-col>
        <span class="body-2 grey--text">{{ $t('host.files.empty') }}</span>
      </v-col>
    </v-row>

    <template v-else>
      <v-row v-for="file in files" :key="file.id">
        <v-col>
          <h6 class="text-uppercase grey--text">
            {{ categoryLabel(file.category) }} · {{ formatDate(file.created_at) }}
          </h6>
          <v-chip
            color="grey lighten-3"
            class="mt-1 px-3"
            :title="file.filename"
            :disabled="openingFileId === file.id"
            @click="openFile(file)"
          >
            <strong class="host-file-name">{{ file.filename }}</strong>
            <v-progress-circular
              v-if="openingFileId === file.id"
              indeterminate
              size="14"
              width="2"
              color="primary"
              class="ml-1"
            />
            <v-icon v-else color="primary" class="ml-1" small>
              mdi-open-in-new
            </v-icon>
          </v-chip>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import { catalogDate } from '@/util/catalog'
import { listHostFiles, hostFileDownloadURL } from '@/services/host/hostService'

export default {
  name: 'HostFiles',

  props: {
    hostId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      files: [],
      isLoading: false,
      openingFileId: null,
      formatDate: catalogDate,
    }
  },

  watch: {
    hostId: {
      immediate: true,
      handler: 'fetchFiles',
    },
  },

  methods: {
    ...mapActions(['showErrorMessage']),

    // The drawer is reused between hosts: a late answer for the previous host must not overwrite
    // the list of the current one.
    async fetchFiles(hostId) {
      this.files = []
      if (!hostId) return

      this.isLoading = true
      try {
        const { data } = await listHostFiles(hostId)
        if (hostId === this.hostId) this.files = data
      } catch (error) {
        if (hostId === this.hostId) this.showErrorMessage(this.errorMessage(error))
      } finally {
        if (hostId === this.hostId) this.isLoading = false
      }
    },

    async openFile(file) {
      this.openingFileId = file.id
      try {
        const { data } = await hostFileDownloadURL(this.hostId, file.id)
        window.open(data.url, '_blank')
      } catch (error) {
        this.showErrorMessage(this.errorMessage(error))
      } finally {
        this.openingFileId = null
      }
    },

    // A category created on the backend before the translation shows its raw name, not the key.
    categoryLabel(category) {
      return this.$t(`host.files.category.${category}`, category)
    },

    errorMessage(error) {
      return error?.data?.message || this.$t('host.files.loadError')
    },
  },
}
</script>

<style scoped>
.host-file-name {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
